// ============================================================
//  Page /sessions/:id/dashboard — vue live
// ============================================================

import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as api from "../lib/api";
import type { GroupeDashboard, SessionDashboard } from "../lib/types";

const POLL_INTERVAL_MS = 5000;

export function SessionDashboardPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<SessionDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastFetch, setLastFetch] = useState<number | null>(null);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<number | null>(null);
  const inFlight = useRef(false);

  async function load() {
    if (!id || inFlight.current) return;
    inFlight.current = true;
    try {
      const d = await api.fetchSessionDashboard(id);
      setData(d);
      setLastFetch(Date.now());
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      inFlight.current = false;
    }
  }

  useEffect(() => {
    load();
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (paused) {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }
    if (!timerRef.current) {
      timerRef.current = window.setInterval(load, POLL_INTERVAL_MS);
    }
    return () => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused]);

  async function onUnblock(groupeId: string, baliseId: string, label: string) {
    if (!id) return;
    if (!confirm(`Lever le blocus sur "${label}" à distance ?`)) return;
    try {
      await api.adminUnblock(id, groupeId, baliseId);
      await load();
    } catch (e) {
      alert("Erreur : " + (e as Error).message);
    }
  }

  if (error && !data) return <div className="page-error">Erreur : {error}</div>;
  if (!data) return <div className="page-loading">Chargement…</div>;

  return (
    <div className="questions-page">
      <header className="questions-head">
        <div>
          <button onClick={() => navigate(`/sessions/${id}`)} className="btn-back">← Retour</button>
          <h1>📊 {data.session.nom}</h1>
          <p className="questions-sub">
            {data.total_groupes} groupe{data.total_groupes > 1 ? "s" : ""} ·
            {" "}{data.nb_actifs} actif{data.nb_actifs > 1 ? "s" : ""} ·
            {" "}<span className={data.nb_blocus_total > 0 ? "metric-warn" : ""}>
              {data.nb_blocus_total} blocus en cours
            </span>
          </p>
        </div>
        <div className="dashboard-controls">
          <button onClick={() => setPaused((p) => !p)} className="btn-secondary">
            {paused ? "▶ Reprendre" : "⏸ Pause"}
          </button>
          <span className="last-fetch">
            {lastFetch ? `Actualisé il y a ${secondsAgo(lastFetch)}s` : ""}
          </span>
        </div>
      </header>

      {data.groupes.length === 0 && (
        <p className="hint">Aucun groupe sur cette session.</p>
      )}

      {data.groupes.length > 0 && (
        <Leaderboard groupes={data.groupes} />
      )}

      <div className="parcours-grid">
        {data.groupes.map((g) => (
          <DashboardCard key={g.groupe.id} g={g} onUnblock={onUnblock} />
        ))}
      </div>
    </div>
  );
}

function Leaderboard({ groupes }: { groupes: GroupeDashboard[] }) {
  const sorted = [...groupes].sort((a, b) => {
    if (b.termine !== a.termine) return b.termine ? 1 : -1; // terminés d'abord
    return (b.points_total ?? 0) - (a.points_total ?? 0);
  });
  return (
    <section className="leaderboard">
      <h2>🏆 Classement live</h2>
      <ol className="leaderboard-list">
        {sorted.map((g, i) => (
          <li key={g.groupe.id} className={`leaderboard-row rank-${i + 1}`}>
            <span className="rank">{i + 1}</span>
            <span className="lb-nom">{g.groupe.nom}</span>
            <code className="code-acces">{g.groupe.code_acces}</code>
            <span className="lb-prog">{g.validees}/{g.total_balises}</span>
            <span className="lb-pts">🏆 {g.points_total ?? 0}</span>
            {g.termine && <span className="badge badge-niveau">✅ fini</span>}
          </li>
        ))}
      </ol>
    </section>
  );
}

function DashboardCard({
  g,
  onUnblock,
}: {
  g: GroupeDashboard;
  onUnblock: (groupeId: string, baliseId: string, label: string) => void;
}) {
  const pct = g.total_balises > 0 ? Math.round((g.validees / g.total_balises) * 100) : 0;
  const hasBlocus = g.blocus.length > 0;
  return (
    <div className={`dash-card ${hasBlocus ? "has-blocus" : ""}`}>
      <header className="dash-card-head">
        <code className="code-acces">{g.groupe.code_acces}</code>
        {g.groupe.niveau && <span className="badge badge-niveau">{g.groupe.niveau}</span>}
      </header>
      <h3 className="dash-card-nom">{g.groupe.nom}</h3>
      <div className="dash-card-parcours">
        🧩 {g.parcours?.nom ?? <span className="muted">parcours supprimé</span>}
      </div>

      <div className="progress-mini">
        <div className="progress-mini-bar">
          <div className="progress-mini-fill" style={{ width: pct + "%" }} />
        </div>
        <span className="progress-mini-label">{g.validees}/{g.total_balises} ({pct}%)</span>
      </div>

      <div className="dash-card-stats">
        {g.en_cours > 0 && <span className="chip chip-yellow">{g.en_cours} en cours</span>}
        {hasBlocus && <span className="chip chip-red">🚧 {g.blocus.length} blocus</span>}
      </div>

      {hasBlocus && (
        <div className="blocus-list">
          {g.blocus.map((b) => (
            <div key={b.balise_id} className="blocus-item">
              <div>
                <div className="blocus-balise">{b.balise_nom ?? <span className="muted">balise sans nom</span>}</div>
                <div className="muted blocus-since">
                  {b.raison === "alea" ? "🎲 Blocus aléa" : "⚠️ Trop d'erreurs"} ·
                  {" depuis "}{relativeTime(b.since)}
                </div>
              </div>
              <button
                onClick={() => onUnblock(g.groupe.id, b.balise_id, b.balise_nom ?? "cette balise")}
                className="btn-unblock"
                title="Lever ce blocus à distance"
              >
                🔓 Lever
              </button>
            </div>
          ))}
        </div>
      )}

      {g.derniere_activite && (
        <div className="dash-card-foot muted">
          Dernière activité {relativeTime(g.derniere_activite)}
        </div>
      )}
    </div>
  );
}

function secondsAgo(ts: number): number {
  return Math.max(0, Math.round((Date.now() - ts) / 1000));
}
function relativeTime(iso: string): string {
  const d = new Date(iso).getTime();
  const s = Math.round((Date.now() - d) / 1000);
  if (s < 60) return "à l'instant";
  if (s < 3600) return `il y a ${Math.round(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.round(s / 3600)} h`;
  return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
}
